/**
 * PropertyDealContext
 *
 * Enhanced deals context that integrates with the new deal library architecture
 * while preserving all existing PropertyPanel functionality and API patterns.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useMemo,
  useEffect,
} from 'react'
// Temporarily comment out library imports to fix opening issue
// import {
//   Deal,
//   CreateDealInput,
//   UpdateDealInput,
//   DealFilters,
//   DealStatus,
//   DealPriority,
//   useDealCRUD,
//   DealCRUDConfig,
// } from '@popety-io/ui'

// Define minimal types for now
interface Deal {
  id: string
  title: string
  [key: string]: any
}

interface CreateDealInput {
  title: string
  [key: string]: any
}

interface UpdateDealInput {
  id: string
  [key: string]: any
}

interface DealFilters {
  [key: string]: any
}

enum DealStatus {
  OPEN = 'open',
  WON = 'won',
  LOST = 'lost',
}

enum DealPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

interface DealCRUDConfig {
  [key: string]: any
}
import { apiClient } from '@/services/apiClient'

// ============================================================================
// Types - Extended from library types for PropertyPanel compatibility
// ============================================================================

/**
 * PropertyPanel-specific deal interface extending the library Deal type
 */
export interface PropertyDeal extends Deal {
  // Legacy API fields for backward compatibility
  pipelineKanbanColumnId: string
  ownerUserId: string
  geoCenter?: string | null
  createTimestamp: string
  updateTimestamp: string
  archiveTimestamp?: string | null
  deleteTimestamp?: string | null
  isArchived?: boolean
  hash?: string | null
  shareInformations?: any
  shareInformationsExpiredTimestamp?: string

  // Property-specific fields
  landId?: string
  buildingId?: string
  propertyType?: 'land' | 'residential' | 'commercial' | 'industrial'

  // Contact information
  firstName?: string
  lastName?: string
  contact?: string
}

/**
 * Deal-Land link interface
 */
export interface DealLand {
  id: string
  pipelineKanbanDealId: string
  landId: string
  createTimestamp: string
  updateTimestamp: string
  deleteTimestamp?: string | null
  isDeleted: boolean
  deal?: PropertyDeal
}

/**
 * Legacy create deal payload for API compatibility
 */
export interface CreateDealPayload {
  title: string
  columnId: string
  informations?: Record<string, any>
  status?: 'open' | 'won' | 'lost'
  participants?: Array<{ id: string; name: string; role?: string }>
}

/**
 * Legacy update deal payload
 */
export interface UpdateDealPayload {
  title?: string
  informations?: Record<string, any>
}

/**
 * Deal participant interface
 */
export interface DealParticipant {
  id: string
  pipelineKanbanDealId: string
  userId: string
  createTimestamp: string
  updateTimestamp: string
  deleteTimestamp?: string | null
  user: {
    id: string
    firstName: string
    lastName: string
    email: string
    photo: string
  }
}

/**
 * Enhanced context state combining library and PropertyPanel functionality
 */
export interface PropertyDealContextState {
  // Legacy PropertyPanel fields
  landId: string | null
  landData: any | null
  deals: DealLand[]
  loading: boolean
  error: string | null
  total: number

  // Enhanced library integration
  propertyDeals: PropertyDeal[]
  filters: DealFilters
  selectedDeals: Set<string>

  // Legacy methods (preserved for backward compatibility)
  fetchDeals: (landId: string) => Promise<void>
  addDeal: (dealData: CreateDealPayload) => Promise<void>
  updateDeal: (dealId: string, data: UpdateDealPayload) => Promise<void>
  deleteDeal: (dealId: string) => Promise<void>
  refreshDeals: () => Promise<void>
  linkDealToLand: (dealId: string, landId: string) => Promise<void>
  unlinkDealFromLand: (dealLandId: string) => Promise<void>
  fetchDealParticipants: (dealId: string) => Promise<DealParticipant[]>
  setLandData: (data: any) => void

  // Enhanced methods using library functionality
  createPropertyDeal: (input: CreateDealInput) => Promise<PropertyDeal>
  updatePropertyDeal: (input: UpdateDealInput) => Promise<PropertyDeal>
  deletePropertyDeal: (dealId: string) => Promise<void>
  setFilters: (filters: DealFilters) => void
  selectDeal: (dealId: string) => void
  deselectDeal: (dealId: string) => void
  clearSelection: () => void
}

// ============================================================================
// Context Creation
// ============================================================================

const PropertyDealContext = createContext<PropertyDealContextState | undefined>(
  undefined
)

// ============================================================================
// Provider Props
// ============================================================================

export interface PropertyDealProviderProps {
  children: ReactNode
  landId?: string | null
  landData?: any | null
  enableLibraryFeatures?: boolean
}

// ============================================================================
// Provider Component
// ============================================================================

export const PropertyDealProvider: React.FC<PropertyDealProviderProps> = ({
  children,
  landId: initialLandId = null,
  landData: initialLandData = null,
  enableLibraryFeatures = true,
}) => {
  // Legacy state
  const [landId, setLandId] = useState<string | null>(initialLandId)
  const [landData, setLandData] = useState<any | null>(initialLandData)
  const [deals, setDeals] = useState<DealLand[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Enhanced state
  const [filters, setFilters] = useState<DealFilters>({})
  const [selectedDeals, setSelectedDeals] = useState<Set<string>>(new Set())

  // ============================================================================
  // Data Transformation Functions (Defined first to avoid initialization issues)
  // ============================================================================

  const transformApiDealToPropertyDeal = useCallback(
    (apiData: any): PropertyDeal => {
      return {
        // Library Deal fields
        id: String(apiData.id || apiData.pipelineKanbanDealId),
        title: apiData.title || '',
        description: apiData.informations?.description || '',
        amount: apiData.informations?.amount || 0,
        currency: apiData.informations?.currency || 'CHF',
        status: mapApiStatusToDealStatus(apiData.status),
        priority: mapApiPriorityToDealPriority(apiData.informations?.priority),
        ownerId: String(apiData.ownerUserId || ''),
        pipelineId: String(apiData.pipelineId || ''),
        stageId: String(apiData.pipelineKanbanColumnId || ''),
        createdAt: apiData.create_timestamp || new Date().toISOString(),
        updatedAt: apiData.update_timestamp || new Date().toISOString(),
        customFields: apiData.informations || {},

        // PropertyPanel legacy fields
        pipelineKanbanColumnId: String(apiData.pipelineKanbanColumnId || ''),
        ownerUserId: String(apiData.ownerUserId || ''),
        geoCenter: apiData.geo_center,
        createTimestamp: apiData.create_timestamp || new Date().toISOString(),
        updateTimestamp: apiData.update_timestamp || new Date().toISOString(),
        archiveTimestamp: apiData.archiveTimestamp,
        deleteTimestamp: apiData.deleteTimestamp,
        isArchived: apiData.isArchived || false,
        hash: apiData.hash,
        shareInformations: apiData.shareInformations,
        shareInformationsExpiredTimestamp:
          apiData.shareInformationsExpiredTimestamp,

        // Property-specific
        landId: String(landId || ''),
        propertyType: apiData.informations?.propertyType || 'land',
        firstName: apiData.firstName,
        lastName: apiData.lastName,
        contact: apiData.contact,
      }
    },
    [landId]
  )

  const transformPropertyDealToApiRequest = useCallback((deal: any): any => {
    return {
      title: deal.title,
      informations: {
        ...deal.customFields,
        ...deal.informations,
        description: deal.description,
        amount: deal.amount,
        currency: deal.currency,
        priority: deal.priority,
        propertyType: deal.propertyType,
      },
      pipelineKanbanColumnId:
        deal.stageId || deal.columnId || deal.pipelineKanbanColumnId,
      ownerUserId: deal.ownerId || deal.ownerUserId,
    }
  }, [])

  // ============================================================================
  // Status/Priority Mapping Functions
  // ============================================================================

  const mapApiStatusToDealStatus = (apiStatus?: string): DealStatus => {
    switch (apiStatus) {
      case 'won':
        return DealStatus.WON
      case 'lost':
        return DealStatus.LOST
      case 'open':
        return DealStatus.OPEN
      default:
        return DealStatus.OPEN
    }
  }

  const mapApiPriorityToDealPriority = (apiPriority?: string): DealPriority => {
    switch (apiPriority) {
      case 'high':
        return DealPriority.HIGH
      case 'urgent':
        return DealPriority.URGENT
      case 'low':
        return DealPriority.LOW
      default:
        return DealPriority.MEDIUM
    }
  }

  // Deal library integration - Now defined after transformation functions
  const dealCRUDConfig: DealCRUDConfig = useMemo(
    () => ({
      endpoints: {
        list: '/pipelines/pipelinekanbandealslands/land',
        get: '/pipelines/kanbandeals/:id',
        create: '/pipelines/kanbandeals',
        update: '/pipelines/kanbandeals/:id',
        delete: '/pipelines/pipelinekanbandealslands/:id',
      },
      apiClient,
      transformResponse: transformApiDealToPropertyDeal,
      transformRequest: transformPropertyDealToApiRequest,
      cache: { enabled: true, ttl: 5 * 60 * 1000 }, // 5 minutes
      optimisticUpdates: true,
      onError: (err) => setError(err.message),
    }),
    [transformApiDealToPropertyDeal, transformPropertyDealToApiRequest]
  )

  // Temporarily disable library integration to fix opening issue
  const libraryState = {
    loading: false,
    error: null,
    deals: [],
    deal: null,
    total: 0,
    filters: {},
    sort: [],
    pagination: { page: 1, pageSize: 20 },
  }
  const libraryActions = {
    loadDeals: async () => {},
    loadDeal: async () => ({}) as any,
    createDeal: async () => ({}) as any,
    updateDeal: async () => ({}) as any,
    deleteDeal: async () => {},
    createDeals: async () => [],
    updateDeals: async () => [],
    deleteDeals: async () => {},
    setFilters: () => {},
    setSort: () => {},
    setPagination: () => {},
    refresh: async () => {},
    reset: () => {},
    clearCache: () => {},
    getCachedDeal: () => null,
  }

  // const [libraryState, libraryActions] = useDealCRUD(
  //   enableLibraryFeatures ? dealCRUDConfig : {}
  // )

  // ============================================================================
  // Legacy Methods (Preserved for Backward Compatibility)
  // ============================================================================

  const fetchDeals = useCallback(
    async (landIdParam: string) => {
      setLoading(true)
      setError(null)
      setLandId(landIdParam)

      try {
        const response = await apiClient.get(
          `/pipelines/pipelinekanbandealslands/land/${landIdParam}`
        )

        const dealLands: DealLand[] = response.data || []
        setDeals(dealLands)

        // Update filters for library integration
        if (enableLibraryFeatures) {
          setFilters((prev) => ({
            ...prev,
            customFilters: { landId: landIdParam },
          }))
        }
      } catch (err: any) {
        console.error('Error fetching deals:', err)
        setError(err.response?.data?.message || 'Failed to fetch deals')
        setDeals([])
      } finally {
        setLoading(false)
      }
    },
    [enableLibraryFeatures]
  )

  const addDeal = useCallback(
    async (dealData: CreateDealPayload) => {
      if (!landId) {
        setError('No land selected')
        return
      }

      setLoading(true)
      setError(null)

      try {
        // Create deal
        const createDealResponse = await apiClient.post(
          `/pipelines/kanbandeals`,
          {
            title: dealData.title,
            informations: dealData.informations || {},
            pipeline_kanban_column_id: dealData.columnId,
          }
        )

        const newDeal = createDealResponse.data

        // Link to land
        const linkResponse = await apiClient.post(
          '/pipelines/pipelinekanbandealslands',
          {
            pipeline_kanban_deal_id: newDeal.id,
            land_id: landId,
          }
        )

        const newDealLand: DealLand = {
          ...linkResponse.data,
          deal: newDeal,
        }

        setDeals((prev) => [...prev, newDealLand])
      } catch (err: any) {
        console.error('Error creating deal:', err)
        setError(err.response?.data?.message || 'Failed to create deal')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [landId]
  )

  const updateDeal = useCallback(
    async (dealId: string, data: UpdateDealPayload) => {
      setLoading(true)
      setError(null)

      try {
        const response = await apiClient.put(
          `/pipelines/kanbandeals/${dealId}`,
          data
        )
        const updatedDeal = response.data

        setDeals((prev) =>
          prev.map((dealLand) =>
            dealLand.pipelineKanbanDealId === dealId
              ? { ...dealLand, deal: { ...dealLand.deal, ...updatedDeal } }
              : dealLand
          )
        )
      } catch (err: any) {
        console.error('Error updating deal:', err)
        setError(err.response?.data?.message || 'Failed to update deal')
        throw err
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const deleteDeal = useCallback(
    async (dealLandId: string) => {
      setLoading(true)
      setError(null)

      try {
        // Find the deal-land association to get dealId and landId
        const dealLand = deals.find((d) => d.id === dealLandId)
        if (!dealLand) {
          throw new Error('Deal-land association not found')
        }

        // Unlink from land using new endpoint with dealId and landId
        await apiClient.delete(
          `/pipelines/pipelinekanbandealslands/deal/${dealLand.pipelineKanbanDealId}/land/${dealLand.landId}`
        )
        setDeals((prev) => prev.filter((d) => d.id !== dealLandId))
      } catch (err: any) {
        console.error('Error deleting deal:', err)
        setError(err.response?.data?.message || 'Failed to delete deal')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [deals]
  )

  const linkDealToLand = useCallback(
    async (dealId: string, landIdParam: string) => {
      setLoading(true)
      setError(null)

      try {
        const response = await apiClient.post(
          '/pipelines/pipelinekanbandealslands',
          {
            pipeline_kanban_deal_id: dealId,
            land_id: landIdParam,
          }
        )

        const dealResponse = await apiClient.get(
          `/pipelines/kanbandeals/${dealId}`
        )

        const newDealLand: DealLand = {
          ...response.data,
          deal: dealResponse.data,
        }

        setDeals((prev) => [...prev, newDealLand])
      } catch (err: any) {
        console.error('Error linking deal to land:', err)
        setError(err.response?.data?.message || 'Failed to link deal to land')
        throw err
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const unlinkDealFromLand = useCallback(
    async (dealLandId: string) => {
      setLoading(true)
      setError(null)

      try {
        // Find the deal-land association to get dealId and landId
        const dealLand = deals.find((d) => d.id === dealLandId)
        if (!dealLand) {
          throw new Error('Deal-land association not found')
        }

        // Unlink from land using new endpoint with dealId and landId
        await apiClient.delete(
          `/pipelines/pipelinekanbandealslands/deal/${dealLand.pipelineKanbanDealId}/land/${dealLand.landId}`
        )
        setDeals((prev) => prev.filter((d) => d.id !== dealLandId))
      } catch (err: any) {
        console.error('Error unlinking deal from land:', err)
        setError(
          err.response?.data?.message || 'Failed to unlink deal from land'
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    [deals]
  )

  const fetchDealParticipants = useCallback(
    async (dealId: string): Promise<DealParticipant[]> => {
      try {
        const response = await apiClient.get(
          `/pipelines/kanbandealparticipants/deal/${dealId}?includeUsers=true`
        )
        return response.data || []
      } catch (err: any) {
        console.error('Error fetching deal participants:', err)
        return []
      }
    },
    []
  )

  const refreshDeals = useCallback(async () => {
    if (landId) {
      await fetchDeals(landId)
    }
  }, [landId, fetchDeals])

  // ============================================================================
  // Enhanced Methods Using Library Functionality
  // ============================================================================

  const createPropertyDeal = useCallback(
    async (input: CreateDealInput): Promise<PropertyDeal> => {
      if (!enableLibraryFeatures) {
        throw new Error('Library features not enabled')
      }

      // Enhance input with property-specific data
      const enhancedInput: CreateDealInput = {
        ...input,
        customFields: {
          ...input.customFields,
          landId,
          propertyType: 'land',
        },
      }

      const deal = await libraryActions.createDeal(enhancedInput)

      // Also create link if landId is available
      if (landId) {
        await linkDealToLand(deal.id, landId)
      }

      return deal as PropertyDeal
    },
    [enableLibraryFeatures, libraryActions, landId, linkDealToLand]
  )

  const updatePropertyDeal = useCallback(
    async (input: UpdateDealInput): Promise<PropertyDeal> => {
      if (!enableLibraryFeatures) {
        throw new Error('Library features not enabled')
      }

      const deal = await libraryActions.updateDeal(input)
      return deal as PropertyDeal
    },
    [enableLibraryFeatures, libraryActions]
  )

  const deletePropertyDeal = useCallback(
    async (dealId: string): Promise<void> => {
      if (!enableLibraryFeatures) {
        throw new Error('Library features not enabled')
      }

      await libraryActions.deleteDeal(dealId)
    },
    [enableLibraryFeatures, libraryActions]
  )

  // ============================================================================
  // Selection Methods
  // ============================================================================

  const selectDeal = useCallback((dealId: string) => {
    setSelectedDeals((prev) => new Set([...prev, dealId]))
  }, [])

  const deselectDeal = useCallback((dealId: string) => {
    setSelectedDeals((prev) => {
      const newSet = new Set(prev)
      newSet.delete(dealId)
      return newSet
    })
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedDeals(new Set())
  }, [])

  // ============================================================================
  // Computed Values
  // ============================================================================

  const propertyDeals = useMemo(() => {
    return deals
      .map((dealLand) =>
        dealLand.deal ? transformApiDealToPropertyDeal(dealLand.deal) : null
      )
      .filter(Boolean) as PropertyDeal[]
  }, [deals, transformApiDealToPropertyDeal])

  // ============================================================================
  // Context Value
  // ============================================================================

  const contextValue: PropertyDealContextState = {
    // Legacy state
    landId,
    landData,
    deals,
    loading: loading || libraryState.loading,
    error: error || libraryState.error?.message || null,
    total: deals.length,

    // Enhanced state
    propertyDeals,
    filters,
    selectedDeals,

    // Legacy methods
    fetchDeals,
    addDeal,
    updateDeal,
    deleteDeal,
    refreshDeals,
    linkDealToLand,
    unlinkDealFromLand,
    fetchDealParticipants,
    setLandData,

    // Enhanced methods
    createPropertyDeal,
    updatePropertyDeal,
    deletePropertyDeal,
    setFilters,
    selectDeal,
    deselectDeal,
    clearSelection,
  }

  return (
    <PropertyDealContext.Provider value={contextValue}>
      {children}
    </PropertyDealContext.Provider>
  )
}

// ============================================================================
// Custom Hook
// ============================================================================

export const usePropertyDeals = () => {
  const context = useContext(PropertyDealContext)
  if (context === undefined) {
    throw new Error(
      'usePropertyDeals must be used within a PropertyDealProvider'
    )
  }
  return context
}

// Export legacy hook name for backward compatibility
export const useDeals = usePropertyDeals
